import { Text, View } from '@react-pdf/renderer';

import { ReachProOutlineLogoPDF } from './ReachProOutlineLogoPDF';
import { styles } from './RetailInvoicePDF.css';
import { RetailInvoicePDFProps } from './RetailInvoicePDF.types';
import {
  formatAmount,
  formatAmountShort,
  formatDate,
} from './RetailInvoicePDF.utils';

// ────────────────────────────────────────────────────────────
// InvoiceHeaderSection
// ────────────────────────────────────────────────────────────

type InvoiceHeaderSectionProps = Pick<
  RetailInvoicePDFProps,
  'status' | 'totalAmount' | 'currency'
>;

export const InvoiceHeaderSection = ({
  status,
  totalAmount,
  currency,
}: InvoiceHeaderSectionProps) => {
  const badgeStyle =
    status === 'Paid' ? styles.statusBadgePaid : styles.statusBadgeOutstanding;

  return (
    <View style={styles.headerSection}>
      {/* Logo placeholder */}
      <Text style={styles.invoiceTitle}>INVOICE</Text>

      {/* Right: INVOICE title + status + total */}
      <View style={styles.invoiceTitleGroup}>
        <View style={badgeStyle}>
          <Text>{status}</Text>
        </View>
        <Text style={styles.totalAmountValue}>
          {formatAmount(totalAmount, currency)}
        </Text>
        <Text style={styles.totalAmountLabel}>Total Invoice Value</Text>
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoiceMetadataRow
// ────────────────────────────────────────────────────────────

type InvoiceMetadataRowProps = Pick<
  RetailInvoicePDFProps,
  'invoiceNumber' | 'invoiceDate' | 'paymentTermsDays' | 'currency'
>;

export const InvoiceMetadataRow = ({
  invoiceNumber,
  invoiceDate,
  paymentTermsDays,
  currency,
}: InvoiceMetadataRowProps) => (
  <View style={styles.metadataRow}>
    <View style={styles.metadataColumn}>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>Invoice #</Text>
        <Text style={styles.metadataValue}>{invoiceNumber}</Text>
      </View>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>Invoice Date</Text>
        <Text style={styles.metadataValue}>{formatDate(invoiceDate)}</Text>
      </View>
    </View>
    <View style={styles.metadataColumn}>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>Payment Terms</Text>
        <Text style={styles.metadataValue}>
          {paymentTermsDays != null ? `${paymentTermsDays} days` : 'N/A'}
        </Text>
      </View>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>Currency</Text>
        <Text style={styles.metadataValue}>{currency}</Text>
      </View>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceAddressSection
// ────────────────────────────────────────────────────────────

type InvoiceAddressSectionProps = Pick<
  RetailInvoicePDFProps,
  | 'brokerName'
  | 'brokerCompany'
  | 'brokerAddress'
  | 'brokerEmail'
  | 'buyerName'
  | 'buyerCompany'
  | 'buyerEmail'
  | 'buyerAddress'
>;

export const InvoiceAddressSection = ({
  brokerName,
  brokerCompany,
  brokerAddress,
  brokerEmail,
  buyerName,
  buyerCompany,
  buyerEmail,
  buyerAddress,
}: InvoiceAddressSectionProps) => (
  <View style={styles.addressSection}>
    {/* FROM */}
    <View style={styles.addressColumn}>
      <Text style={styles.addressSectionTitle}>FROM</Text>
      <Text style={styles.addressName}>{brokerName}</Text>
      {brokerCompany ? (
        <Text style={styles.addressDetail}>{brokerCompany}</Text>
      ) : null}
      {brokerAddress ? (
        <Text style={styles.addressDetail}>{brokerAddress}</Text>
      ) : null}
      {brokerEmail ? (
        <Text style={styles.addressDetail}>{brokerEmail}</Text>
      ) : null}
    </View>

    {/* BILLED TO */}
    <View style={styles.addressColumnRight}>
      <Text style={styles.addressSectionTitle}>BILLED TO</Text>
      {buyerName ? (
        <Text style={styles.addressName}>{buyerName}</Text>
      ) : (
        <Text style={styles.addressName}>—</Text>
      )}
      {buyerCompany ? (
        <Text style={styles.addressDetail}>{buyerCompany}</Text>
      ) : null}
      {buyerAddress ? (
        <Text style={styles.addressDetail}>{buyerAddress}</Text>
      ) : null}
      {buyerEmail ? (
        <Text style={styles.addressDetail}>{buyerEmail}</Text>
      ) : null}
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceOrderDetailTable
// ────────────────────────────────────────────────────────────

type InvoiceOrderDetailTableProps = Pick<
  RetailInvoicePDFProps,
  | 'lineItems'
  | 'totalAmount'
  | 'balanceDue'
  | 'balanceDueDate'
  | 'currency'
  | 'status'
  | 'paymentsMade'
>;

export const InvoiceOrderDetailTable = ({
  lineItems,
  totalAmount,
  balanceDue,
  balanceDueDate,
  currency,
  status,
  paymentsMade,
}: InvoiceOrderDetailTableProps) => {
  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const computedPaymentsMade = paymentsMade ?? subtotal - balanceDue;

  const isPaid = status === 'Paid';
  const rowStyle = isPaid ? styles.balanceDueRowPaid : styles.balanceDueRow;
  const labelStyle = isPaid
    ? styles.balanceDueLabelPaid
    : styles.balanceDueLabel;
  const dateStyle = isPaid ? styles.balanceDueDatePaid : styles.balanceDueDate;
  const amountStyle = isPaid
    ? styles.balanceDueAmountPaid
    : styles.balanceDueAmount;

  return (
    <View style={styles.orderDetailSection}>
      <Text style={styles.orderDetailTitle}>ORDER DETAIL</Text>

      {/* Table 1: line items only */}
      <View style={styles.tableContainer}>
        {/* Header */}
        <View style={styles.tableHeaderRow}>
          <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Overview</Text>
          <Text style={[styles.tableHeaderCellRight, { flex: 1 }]}>Rate %</Text>
          <Text style={[styles.tableHeaderCellRight, { flex: 1.5 }]}>
            Amount $
          </Text>
        </View>

        {/* Flat line items */}
        {lineItems.map((item, index) => {
          const rowStyle = index % 2 === 1 ? styles.tableRowAlt : {};

          return (
            <View key={index} style={[styles.tableRow, rowStyle]}>
              <View style={[styles.tableCell, { flex: 3 }]}>
                <Text>{item.description || '—'}</Text>
                {item.paymentReferenceId ? (
                  <Text style={{ fontSize: 8, opacity: 0.6 }}>
                    Ref: {item.paymentReferenceId}
                  </Text>
                ) : null}
              </View>
              <Text style={styles.tableCellRate}>
                {item.ratePercent != null ? `${item.ratePercent} %` : '—'}
              </Text>
              <Text style={styles.tableCellAmount}>
                {formatAmountShort(item.amount)}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Table 2: totals */}
      <View style={[styles.tableContainer, { marginTop: 8 }]}>
        {/* Subtotal row */}
        <View style={styles.subtotalRow}>
          <Text style={styles.subtotalLabel}>Subtotal</Text>
          <View style={{ flex: 1 }} />
          <Text style={styles.subtotalAmount}>
            {formatAmountShort(subtotal)}
          </Text>
        </View>
        {/* Payments row */}
        {computedPaymentsMade > 0 ? (
          <View
            style={[
              styles.subtotalRow,
              {
                borderTopWidth: 1,
                borderTopColor: '#E5E7EB',
                borderTopStyle: 'solid',
              },
            ]}
          >
            <Text style={styles.subtotalLabel}>Payments</Text>
            <View style={{ flex: 1 }} />
            <Text style={styles.subtotalAmount}>
              -{formatAmountShort(computedPaymentsMade)}
            </Text>
          </View>
        ) : null}
        {/* Balance Due row */}
        <View
          style={[
            rowStyle,
            {
              borderTopWidth: 1,
              borderTopColor: '#E5E7EB',
              borderTopStyle: 'solid',
            },
          ]}
        >
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <Text style={labelStyle}>Balance Due</Text>
            {balanceDueDate ? (
              <Text style={dateStyle}> • {formatDate(balanceDueDate)}</Text>
            ) : null}
          </View>
          <Text style={amountStyle}>{formatAmount(balanceDue, currency)}</Text>
        </View>
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoicePaymentInfoSection
// ────────────────────────────────────────────────────────────

type InvoicePaymentInfoSectionProps = Pick<
  RetailInvoicePDFProps,
  'paymentMethod' | 'paymentReference'
>;

export const InvoicePaymentInfoSection = ({
  paymentMethod,
  paymentReference,
}: InvoicePaymentInfoSectionProps) => (
  <View style={styles.paymentInfoSection}>
    <Text style={styles.paymentInfoTitle}>{'PAYMENT INFO & TERMS'}</Text>

    {paymentMethod ? (
      <View style={styles.paymentInfoRow}>
        <Text style={styles.paymentInfoLabel}>Payment Method</Text>
        <Text style={styles.paymentInfoValue}>{paymentMethod}</Text>
      </View>
    ) : null}

    <View style={styles.paymentInfoRow}>
      <Text style={styles.paymentInfoLabel}>Instructions</Text>
      <Text style={styles.paymentInfoValue}>
        Paying remaining balance via agreed payment method.
      </Text>
    </View>

    {paymentReference ? (
      <View style={styles.paymentInfoRow}>
        <Text style={styles.paymentInfoLabel}>Payment Reference</Text>
        <Text style={styles.paymentInfoValue}>{paymentReference}</Text>
      </View>
    ) : null}

    <View style={styles.paymentInfoRow}>
      <Text style={styles.paymentInfoLabel}>Refund Policy</Text>
      <Text style={styles.paymentInfoValue}>All sales are final.</Text>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceBrokerNotesSection
// ────────────────────────────────────────────────────────────

type InvoiceBrokerNotesSectionProps = Pick<
  RetailInvoicePDFProps,
  'brokerNotes'
>;

export const InvoiceBrokerNotesSection = ({
  brokerNotes,
}: InvoiceBrokerNotesSectionProps) => {
  if (!brokerNotes) return null;

  return (
    <View style={styles.brokerNotesSection}>
      <Text style={styles.brokerNotesTitle}>BROKER NOTES</Text>
      <Text style={styles.brokerNotesText}>{brokerNotes}</Text>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoiceFooter
// ────────────────────────────────────────────────────────────

export const InvoiceFooter = () => (
  <View style={styles.footer}>
    <ReachProOutlineLogoPDF width={24} height={24} color="#221e1f" />
  </View>
);
